// Rich text editor for notes — contentEditable with a small toolbar.
// Uses execCommand for simplicity (still works everywhere we ship to).

import React, { useRef, useState } from "react";
import { Ic } from "./Icon";

interface Props {
  placeholder?: string;
  onSubmit?: (html: string) => void;
  height?: number;
}

export function RichTextEditor({
  placeholder = "Add a note…", onSubmit, height = 88,
}: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [empty, setEmpty] = useState(true);
  const [focused, setFocused] = useState(false);

  const exec = (cmd: string, arg?: string) => {
    document.execCommand(cmd, false, arg);
    ref.current?.focus();
    refresh();
  };

  const refresh = () => {
    const el = ref.current;
    if (!el) return;
    setEmpty(!(el.innerText || "").trim() && !el.querySelector("img"));
  };

  const insertImage = (dataUrl: string) => {
    exec("insertImage", dataUrl);
    const imgs = ref.current?.querySelectorAll("img") ?? [];
    const last = imgs[imgs.length - 1] as HTMLImageElement | undefined;
    if (last) {
      last.style.maxWidth = "100%";
      last.style.borderRadius = "6px";
      last.style.margin = "6px 0";
    }
  };

  const onPickImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => insertImage(String(ev.target?.result));
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const onPaste = (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const it of items) {
      if (it.type?.startsWith("image/")) {
        e.preventDefault();
        const file = it.getAsFile();
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => insertImage(String(ev.target?.result));
        reader.readAsDataURL(file);
        return;
      }
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      const html = ref.current?.innerHTML ?? "";
      onSubmit?.(html);
      if (ref.current) ref.current.innerHTML = "";
      setEmpty(true);
    }
  };

  return (
    <div className={"rte" + (focused ? " focused" : "")}>
      <div className="rte-toolbar">
        <button className="rte-btn" onMouseDown={(e) => { e.preventDefault(); exec("bold"); }} title="Bold (⌘B)">
          <span style={{ fontWeight: 700 }}>B</span>
        </button>
        <button className="rte-btn" onMouseDown={(e) => { e.preventDefault(); exec("italic"); }} title="Italic (⌘I)">
          <span style={{ fontStyle: "italic" }}>I</span>
        </button>
        <span className="rte-sep" />
        <button className="rte-btn" onMouseDown={(e) => { e.preventDefault(); exec("insertUnorderedList"); }} title="Bulleted list">• ≡</button>
        <button className="rte-btn" onMouseDown={(e) => { e.preventDefault(); exec("insertOrderedList"); }} title="Numbered list">1. ≡</button>
        <button className="rte-btn" onMouseDown={(e) => { e.preventDefault(); exec("formatBlock", "blockquote"); }} title="Quote">&ldquo; &rdquo;</button>
        <span className="rte-sep" />
        <button className="rte-btn" onMouseDown={(e) => { e.preventDefault(); fileRef.current?.click(); }} title="Insert image">
          <Ic name="file" size={13} />
        </button>
        <input ref={fileRef} type="file" accept="image/*" onChange={onPickImage} style={{ display: "none" }} />
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 10.5, color: "var(--ink-4)" }}>
          paste images · <span className="kbd">⌘↵</span> to post
        </span>
      </div>
      <div
        ref={ref}
        className="rte-area"
        contentEditable
        suppressContentEditableWarning
        style={{ minHeight: height }}
        onInput={refresh}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
      />
      {empty && <div className="rte-placeholder">{placeholder}</div>}
    </div>
  );
}
